import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type PlotPanelState } from "@/shared/state/visualization";
import { DEFAULT_PLOT_PANEL_CONFIG } from "@/shared/state/visualization/context";
import { PlotYAxis } from "@/shared/state/visualization/schema/v1";

import { type PanelSettingsSidebarProps } from "../../sidebarContext";
import { BasePanelSettings } from "../BasePanelSettings";
import { SettingsSection } from "../SettingsSection";
import styles from "../SettingsSection.module.css";
import { Spotlight } from "../Spotlight";

import { AddSeries } from "./AddSeries";
import { AxisLabelInput } from "./AxisLabelInput";
import { AxisRangeInput } from "./AxisRangeInput";
import { ConfigItem } from "./ConfigItem";
import { PlotSeriesList } from "./PlotSeriesList";
import { SelectLegendPosition } from "./SelectLegendPosition";
import { SelectTimeBoundsSource } from "./SelectTimeBoundsSource";
import { ShowPlayheadToggle } from "./ShowPlayheadToggle";
import { XAxisAlignmentConfig } from "./XAxisAlignmentConfig";

export interface PlotPanelSettingsSidebarProps extends PanelSettingsSidebarProps {
  field?: "Series";
  /**
   * Spotlights one series' row instead of the Add Series button: the row
   * expands, scrolls into view, and pulses. Only read when `field` is
   * `"Series"`.
   */
  seriesId?: string;
}

interface PlotPanelSettingsProps extends PlotPanelSettingsSidebarProps {
  state: PlotPanelState;
}

export function PlotPanelSettings({
  field,
  seriesId,
  state,
  spotlightKey,
}: PlotPanelSettingsProps) {
  const extraYAxes = state.config?.extraYAxes ?? {};
  const xAxisAlignment = state.config?.xAxisAlignment;
  const alignmentEnabled = useCheckFeatureFlag("release-plot-x-axis-alignment");
  return (
    <BasePanelSettings state={state}>
      <SettingsSection title="Axes">
        <ConfigItem label="Time Range">
          <SelectTimeBoundsSource
            disabled={xAxisAlignment !== undefined}
            panelId={state.id}
            timeBoundsSource={
              state.config?.timeBoundsSource ??
              DEFAULT_PLOT_PANEL_CONFIG.timeBoundsSource
            }
          />
        </ConfigItem>
        <ConfigItem label="X-Axis">
          <XAxisAlignmentConfig
            enabled={alignmentEnabled}
            panelId={state.id}
            xAxisAlignment={xAxisAlignment}
          />
          <AxisLabelInput
            axis="x"
            currentAxisConfig={state.config?.xAxis}
            panelId={state.id}
          />
        </ConfigItem>
        <ConfigItem label="Y-Axis">
          <AxisLabelInput
            axis="y"
            currentAxisConfig={state.config?.yAxis}
            panelId={state.id}
          />
          <AxisRangeInput
            currentBounds={state.config?.yAxis?.bounds}
            panelId={state.id}
            axis="y"
            state={state}
          />
        </ConfigItem>
        {Object.keys(extraYAxes).length > 0 &&
          Object.keys(extraYAxes)
            .sort()
            .map((k) => {
              const key = k as PlotYAxis;
              const label =
                key === "y"
                  ? "Default Y-Axis (Left)"
                  : `Y-Axis ${key.substring(1, key.length - 1)} (${key.endsWith("r") ? "Right" : "Left"})`;

              return (
                <ConfigItem label={label} key={key}>
                  <AxisLabelInput
                    axis={key}
                    currentAxisConfig={state.config?.extraYAxes?.[key]}
                    panelId={state.id}
                  />
                  <AxisRangeInput
                    axis={key}
                    currentBounds={state.config?.extraYAxes?.[key]?.bounds}
                    panelId={state.id}
                    state={state}
                  />
                </ConfigItem>
              );
            })}
      </SettingsSection>
      <SettingsSection title="Legend">
        <ConfigItem label="Position">
          <SelectLegendPosition
            panelId={state.id}
            legendPosition={
              state.config?.legendPosition ??
              DEFAULT_PLOT_PANEL_CONFIG.legendPosition
            }
          />
        </ConfigItem>
      </SettingsSection>
      <SettingsSection title="Playback">
        <ShowPlayheadToggle
          panelId={state.id}
          showPlayhead={
            state.config?.showPlayhead ?? DEFAULT_PLOT_PANEL_CONFIG.showPlayhead
          }
        />
      </SettingsSection>
      <SettingsSection title="Series">
        <PlotSeriesList
          state={state}
          spotlightSeriesId={field === "Series" ? seriesId : undefined}
          spotlightKey={spotlightKey}
        />
        <Spotlight
          className={styles.settingContainer}
          spotlightKey={
            field === "Series" && seriesId === undefined
              ? spotlightKey
              : undefined
          }
        >
          <AddSeries plotPanel={state} />
        </Spotlight>
      </SettingsSection>
    </BasePanelSettings>
  );
}
